/**
 * Marketing → YouTube: each episode as a YouTube Short to upload by hand in
 * YouTube Studio — the video, then the title, description and tags to paste,
 * and the settings to pick. The same content as Marketing → Content, arranged
 * for YouTube; the words come from youtube() in @shared-ui/whats-wrong.
 *
 * Nothing publishes from here yet: YouTube's upload API needs a person's
 * consent and keeps an unaudited app's uploads private
 * (marketing-console-target.md, "YouTube").
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@shared-ui/components';
import { youtube } from '@shared-ui/whats-wrong/episodes.js';
import { marketingApi } from '../../api/marketingApi';
import { AssetTile, CopyField, MissingTile } from './MarketingParts';
import { SERIES, episodesWithFiles, pick } from './episodeContent';
import './Marketing.css';

/** The same for every Short; picked in Studio's upload dialog. */
const SETTINGS = [
  'Audience: No, it\'s not made for kids',
  'Category: Education',
  'Visibility: Public',
  'Playlist: What\'s wrong?',
].join('\n');

export default function MarketingYouTube() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['marketing-assets', SERIES.id],
    queryFn: () => marketingApi.assets(SERIES.id),
    staleTime: 30 * 60 * 1000,
  });
  const episodes = useMemo(() => episodesWithFiles(data?.files ?? []), [data]);

  return (
    <div className="mk-page">
      <PageHeader
        title="YouTube"
        subtitle="Each episode as a Short: upload the video in YouTube Studio, then paste the words and pick the settings."
      />
      {error && <p className="mk-error">Could not load the files: {(error as Error).message}</p>}
      {isLoading && <p className="mk-sub">Loading files…</p>}

      {episodes.map(({ episode, files }) => {
        const yt = youtube(episode);
        const short = pick(files, 'short', 'short');
        return (
          <section key={episode.id} className="mk-episode">
            <header className="mk-episode-head">
              <span className="mk-num">#{episode.id}</span>
              <div><h2>{episode.title}</h2><span className="mk-sub">{episode.topic}</span></div>
            </header>
            <div className="mk-episode-body">
              <ol className="mk-slots">
                <li>
                  {short
                    ? <AssetTile asset={short} label="Short" />
                    : <MissingTile label="Short" hint="Render with --format=short" />}
                </li>
              </ol>
              <div className="mk-words" aria-label={`#${episode.id} words`}>
                <CopyField label={`Title (${yt.title.length}/100)`} text={yt.title} />
                <CopyField label="Description" text={yt.description} />
                <CopyField label="Tags" text={yt.tags.join(', ')} />
                <CopyField label="Settings" text={SETTINGS} />
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
